import { FormEvent, useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"

import { useLazyLoginQuery } from "store/api/authApi"
import { useLazyGetThemeQuery } from "store/api/dashboardApi"
import { useLazyGetUserProfileQuery } from "store/api/profileApi"
import { useAppDispatch } from "store/hooks"
import { setUserActiveTheme, setUserLogo } from "store/tempDataSlise"
import { setUserAuthorized, setUserProfile, setUserTemperatureScale } from "store/userSlise"
import { ContractorThemeKey } from "constants/ContractorThemes"
import { StorageKeys } from "types/enums/StorageKeys"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import getErrorMessage from "utils/getErrorMessage"
import { getStorageItem, setStorageItem } from "utils/localStorage"
import { onFormValidate, signInSchema, validationInitialState } from "utils/onValidate"
import { Spinner } from "components/common/Spinner"
import { AuthForm } from "components/form/AuthForm"
import { Button } from "components/form/Button"
import { Input } from "components/form/Input"

import styles from "./index.module.scss"

const inputValuesInitialState = {
  email: "",
  password: "",
}

export const SignIn = () => {
  const [triggerLogin, { isFetching, data }] = useLazyLoginQuery()
  const [triggerGetProfile] = useLazyGetUserProfileQuery()
  const [triggerGetTheme] = useLazyGetThemeQuery()
  const [inputValues, setInputValues] = useState(inputValuesInitialState)
  const dispatch = useAppDispatch()

  const [error, setError] = useState(validationInitialState)

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const handleThemeChange = (themeKey?: ContractorThemeKey, logo?: string | null) => {
    dispatch(setUserActiveTheme(themeKey || "default"))
    dispatch(setUserLogo(logo || undefined))
  }

  const getProfile = async () => {
    try {
      const res = await triggerGetProfile()
      if (!res.data?.data) {
        return
      }

      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        const lastSync = dayjs()
        const data = { ...res?.data?.data, lastSync }
        const userTemperatureScale = getStorageItem(StorageKeys.UserTemperatureScale)
        const temperatureScale = data?.contractor?.country?.id === 1 ? "fahrenheit" : "celsius"
        setStorageItem(StorageKeys.UserProfile, data)

        if (!userTemperatureScale) {
          setStorageItem(StorageKeys.UserTemperatureScale, temperatureScale)
        }
        dispatch(setUserProfile(data))
        dispatch(setUserAuthorized(true))
        dispatch(setUserTemperatureScale(userTemperatureScale || temperatureScale))

        const contractor = res.data?.data?.contractor

        if (contractor && contractor.subscription_plan === SubscriptionPlan.E_PLUS) {
          const themeResult = await triggerGetTheme()
          if (themeResult.data?.theme) {
            try {
              const theme = JSON.parse(themeResult.data.theme)
              handleThemeChange(theme.themeKey, themeResult.data.logo)
            } catch {
              handleThemeChange()
            }
          } else if (themeResult.data?.logo) {
            handleThemeChange(undefined, themeResult.data.logo)
          } else {
            handleThemeChange()
          }
        } else {
          handleThemeChange()
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (onFormValidate(inputValues, signInSchema, setError)) {
      try {
        const res = await triggerLogin({
          username: inputValues.email,
          password: inputValues.password,
        })
        const error = getErrorMessage(res?.error)

        if (error) {
          toast.error(error)
        }
      } catch (e) {
        const error = e as Error
        toast.error(error?.message)
      }
    }
  }

  useEffect(() => {
    if (data?.token) {
      setStorageItem(StorageKeys.Token, data.token)
      getProfile()
      setInputValues(inputValuesInitialState)
    }
  }, [data?.token])

  return (
    <>
      <Helmet>
        <title>Nuve - SignIn</title>
      </Helmet>
      <div>
        <AuthForm title="Log In" subtitle="Welcome back! Please enter your details." onSubmit={onSubmit}>
          <Input
            label="Email / Username"
            placeholder="Enter email or username"
            name="email"
            testId="sign-in-email"
            value={inputValues.email}
            onChange={onInputChange}
            errorMessage={error.field === "email" ? error.message : ""}
          />
          <Input
            label="Password"
            placeholder="Enter password"
            inputType="password"
            name="password"
            testId="sign-in-password"
            value={inputValues.password}
            onChange={onInputChange}
            errorMessage={error.field === "password" ? error.message : ""}
          />
          <Link className={styles.forgotPasswordLink} to="/auth/forgot-password">
            Forgot password
          </Link>
          <div className={styles.authButtonsWrapper}>
            <Button
              onClick={onSubmit}
              type="submit"
              disabled={isFetching}
              color="colored"
              size="fullWidth"
              testId="sign-in-submit"
            >
              {isFetching ? <Spinner width={38} height={38} /> : "Log in"}
            </Button>
          </div>
        </AuthForm>
      </div>
    </>
  )
}
